import React, { useState, useEffect } from 'react';
import { summerJobs } from '../lib/supabase';
import summerJobsData from '../data/summer-employment-jamaica.json';

export const SummerEmploymentPage = ({ setPage, goBack, viewerMode = 'student' }) => {
  const [activeTab, setActiveTab] = useState('programs');
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jobsByType, setJobsByType] = useState({
    formal: [],
    tourism: [],
    retail: [],
    bpo: [],
    community: [],
    agricultural: []
  });

  const isStudent = viewerMode === 'student';

  // Load data from Supabase with JSON fallback
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const { data, error } = await summerJobs.getAll();
        if (!error && data && data.length > 0) {
          // Group database data by job_type
          const grouped = {
            formal: data.filter(j => j.job_type === 'formal').map(transformDbJob),
            tourism: data.filter(j => j.job_type === 'tourism').map(transformDbJob),
            retail: data.filter(j => j.job_type === 'retail').map(transformDbJob),
            bpo: data.filter(j => j.job_type === 'bpo').map(transformDbJob),
            community: data.filter(j => j.job_type === 'community').map(transformDbJob),
            agricultural: data.filter(j => j.job_type === 'agricultural').map(transformDbJob)
          };
          setJobsByType(grouped);
        } else {
          // Fallback to JSON data
          setJobsByType({
            formal: summerJobsData.formal_summer_programs || [],
            tourism: summerJobsData.tourism_summer_jobs || [],
            retail: summerJobsData.retail_summer_jobs || [],
            bpo: summerJobsData.bpo_summer_jobs || [],
            community: summerJobsData.community_programs || [],
            agricultural: summerJobsData.agricultural_summer_work || []
          });
        }
      } catch (err) {
        console.error('Error loading summer jobs:', err);
        // Fallback to JSON data
        setJobsByType({
          formal: summerJobsData.formal_summer_programs || [],
          tourism: summerJobsData.tourism_summer_jobs || [],
          retail: summerJobsData.retail_summer_jobs || [],
          bpo: summerJobsData.bpo_summer_jobs || [],
          community: summerJobsData.community_programs || [],
          agricultural: summerJobsData.agricultural_summer_work || []
        });
      }
      setLoading(false);
    };
    loadData();
  }, []);

  // Transform database record to expected format
  const transformDbJob = (item) => ({
    id: item.id,
    name: item.name,
    organization: item.organization,
    description: item.description,
    locations: item.locations || [],
    age_requirement: item.age_requirement,
    duration: item.duration,
    pay: item.pay,
    paid: item.is_paid !== undefined ? item.is_paid : item.paid,
    positions_available: item.positions || item.positions_available || [],
    eligibility: item.eligibility || [],
    requirements: item.requirements || [],
    how_to_apply: {
      method: item.application_method,
      timeline: item.application_timeline,
      contact: item.application_contact,
      requirements: item.application_requirements || []
    },
    what_you_gain: item.what_you_gain || [],
    tips: item.tips || []
  });

  const tabs = [
    { id: 'programs', name: 'Formal Programs', icon: '🏛️' },
    { id: 'tourism', name: 'Tourism Jobs', icon: '🏨' },
    { id: 'retail', name: 'Retail & Food', icon: '🛒' },
    { id: 'bpo', name: 'BPO Jobs', icon: '📞' },
    { id: 'community', name: 'Community', icon: '🤝' },
    { id: 'tips', name: 'Application Tips', icon: '📝' }
  ];

  const renderOpportunityCard = (opp, type) => (
    <div
      key={opp.id || opp.name}
      className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => setSelectedOpportunity({ ...opp, opportunityType: type })}
    >
      <div className="flex justify-between items-start mb-2">
        <h3 className="text-lg font-bold text-gray-800">{opp.name}</h3>
        {opp.paid !== undefined && (
          <span className={`text-xs px-2 py-1 rounded ${opp.paid ? 'bg-brand-100 text-brand-700' : 'bg-gray-100 text-gray-600'}`}>
            {opp.paid ? 'Paid' : 'Unpaid/Stipend'}
          </span>
        )}
      </div>

      {opp.organization && (
        <p className="text-sm text-gray-600 mb-2">{opp.organization}</p>
      )}

      {opp.description && (
        <p className="text-gray-700 text-sm mb-3">{opp.description}</p>
      )}

      <div className="space-y-1 text-sm">
        {opp.locations && (
          <div className="flex items-center gap-2">
            <span className="text-blue-600">📍</span>
            <span>{Array.isArray(opp.locations) ? opp.locations.join(', ') : opp.locations}</span>
          </div>
        )}
        {opp.age_requirement && (
          <div className="flex items-center gap-2">
            <span className="text-purple-600">👤</span>
            <span>Age: {opp.age_requirement}</span>
          </div>
        )}
        {opp.duration && (
          <div className="flex items-center gap-2">
            <span className="text-brand-600">⏰</span>
            <span>{opp.duration}</span>
          </div>
        )}
        {opp.pay && (
          <div className="flex items-center gap-2">
            <span className="text-yellow-600">💰</span>
            <span>{opp.pay}</span>
          </div>
        )}
      </div>

      {opp.positions_available && (
        <div className="mt-3 flex flex-wrap gap-1">
          {opp.positions_available.slice(0, 3).map((pos, idx) => (
            <span key={idx} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
              {pos}
            </span>
          ))}
          {opp.positions_available.length > 3 && (
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
              +{opp.positions_available.length - 3} more
            </span>
          )}
        </div>
      )}

      <div className="mt-4 text-blue-600 font-medium text-sm">
        View details →
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-600 text-white py-6 sm:py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={goBack}
            className="mb-3 sm:mb-4 text-orange-100 hover:text-white flex items-center gap-2 text-sm sm:text-base"
          >
            ← Back
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Summer Employment</h1>
          <p className="text-orange-100">
            {isStudent
              ? summerJobsData.introduction.student_view
              : summerJobsData.introduction.parent_view}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4 -mt-4">
        <div className="bg-white rounded-lg shadow-md p-1 flex flex-wrap gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSelectedOpportunity(null); }}
              className={`flex-1 min-w-[80px] py-3 px-2 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-1 ${
                activeTab === tab.id
                  ? 'bg-orange-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span>{tab.icon}</span>
              <span className="hidden sm:inline text-xs">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Selected Opportunity Detail */}
        {selectedOpportunity && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <button
              onClick={() => setSelectedOpportunity(null)}
              className="mb-4 text-orange-600 hover:text-orange-800 flex items-center gap-2"
            >
              ← Back to list
            </button>

            <h2 className="text-2xl font-bold text-gray-800 mb-2">{selectedOpportunity.name}</h2>
            {selectedOpportunity.organization && (
              <p className="text-gray-600 mb-4">{selectedOpportunity.organization}</p>
            )}

            {selectedOpportunity.description && (
              <p className="text-gray-700 mb-6">{selectedOpportunity.description}</p>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              {/* Key Info */}
              <div className="bg-orange-50 rounded-lg p-4">
                <h3 className="font-semibold text-orange-800 mb-3">Key Information</h3>
                <div className="space-y-2 text-sm">
                  {selectedOpportunity.age_requirement && (
                    <div><strong>Age:</strong> {selectedOpportunity.age_requirement}</div>
                  )}
                  {selectedOpportunity.duration && (
                    <div><strong>Duration:</strong> {selectedOpportunity.duration}</div>
                  )}
                  {selectedOpportunity.pay && (
                    <div><strong>Pay:</strong> {selectedOpportunity.pay}</div>
                  )}
                  {selectedOpportunity.locations && (
                    <div><strong>Locations:</strong> {Array.isArray(selectedOpportunity.locations) ? selectedOpportunity.locations.join(', ') : selectedOpportunity.locations}</div>
                  )}
                </div>
              </div>

              {/* Positions */}
              {selectedOpportunity.positions_available && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-800 mb-3">Available Positions</h3>
                  <ul className="space-y-1">
                    {selectedOpportunity.positions_available.map((pos, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                        {pos}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Eligibility */}
            {selectedOpportunity.eligibility && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-800 mb-3">Eligibility Requirements</h3>
                <ul className="space-y-2">
                  {selectedOpportunity.eligibility.map((req, idx) => (
                    <li key={idx} className="flex items-start gap-2 bg-gray-50 p-3 rounded-lg">
                      <span className="text-brand-600">✓</span>
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Requirements */}
            {selectedOpportunity.requirements && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-800 mb-3">Requirements</h3>
                <ul className="space-y-2">
                  {selectedOpportunity.requirements.map((req, idx) => (
                    <li key={idx} className="flex items-start gap-2 bg-gray-50 p-3 rounded-lg">
                      <span className="text-blue-600">→</span>
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* How to Apply */}
            {selectedOpportunity.how_to_apply && (
              <div className="mt-6 bg-brand-50 rounded-lg p-4">
                <h3 className="font-semibold text-brand-800 mb-3">How to Apply</h3>
                <div className="space-y-2 text-sm">
                  {selectedOpportunity.how_to_apply.timeline && (
                    <div><strong>When:</strong> {selectedOpportunity.how_to_apply.timeline}</div>
                  )}
                  {selectedOpportunity.how_to_apply.method && (
                    <div><strong>How:</strong> {selectedOpportunity.how_to_apply.method}</div>
                  )}
                  {selectedOpportunity.how_to_apply.contact && (
                    <div><strong>Contact:</strong> {selectedOpportunity.how_to_apply.contact}</div>
                  )}
                  {selectedOpportunity.how_to_apply.requirements && (
                    <div className="mt-3">
                      <strong>You'll Need:</strong>
                      <ul className="mt-2 space-y-1">
                        {selectedOpportunity.how_to_apply.requirements.map((req, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-brand-600 rounded-full"></span>
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* What You Gain */}
            {selectedOpportunity.what_you_gain && (
              <div className="mt-6 bg-purple-50 rounded-lg p-4">
                <h3 className="font-semibold text-purple-800 mb-3">What You'll Gain</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedOpportunity.what_you_gain.map((gain, idx) => (
                    <span key={idx} className="bg-purple-100 text-purple-700 px-3 py-1 rounded-lg text-sm">
                      {gain}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Tips */}
            {selectedOpportunity.tips && (
              <div className="mt-6 bg-yellow-50 rounded-lg p-4">
                <h3 className="font-semibold text-yellow-800 mb-3">Tips</h3>
                <ul className="space-y-1">
                  {selectedOpportunity.tips.map((tip, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <span className="text-yellow-600">★</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading opportunities...</p>
          </div>
        )}

        {/* Formal Programs Tab */}
        {activeTab === 'programs' && !selectedOpportunity && !loading && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-800">Formal Summer Employment Programs</h2>
            <p className="text-gray-600">Structured programs from reputable organizations - great for building your resume.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {jobsByType.formal.map(program => renderOpportunityCard(program, 'formal'))}
            </div>
          </div>
        )}

        {/* Tourism Tab */}
        {activeTab === 'tourism' && !selectedOpportunity && !loading && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-800">Tourism & Hospitality Jobs</h2>
            <p className="text-gray-600">Jamaica's tourism industry offers many summer opportunities.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {jobsByType.tourism.map(job => renderOpportunityCard(job, 'tourism'))}
            </div>
          </div>
        )}

        {/* Retail Tab */}
        {activeTab === 'retail' && !selectedOpportunity && !loading && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-800">Retail & Food Service Jobs</h2>
            <p className="text-gray-600">Entry-level positions at stores and restaurants island-wide.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {jobsByType.retail.map(job => renderOpportunityCard(job, 'retail'))}
            </div>
          </div>
        )}

        {/* BPO Tab */}
        {activeTab === 'bpo' && !selectedOpportunity && !loading && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-800">BPO & Call Center Jobs</h2>
            <p className="text-gray-600">Higher paying entry-level jobs with flexible schedules.</p>
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> BPO jobs typically pay J$20,000-35,000+ per week and offer various shift options that can work around school schedules.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {jobsByType.bpo.map(job => renderOpportunityCard(job, 'bpo'))}
            </div>
          </div>
        )}

        {/* Community Tab */}
        {activeTab === 'community' && !selectedOpportunity && !loading && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-800">Community & Volunteer Programs</h2>
            <p className="text-gray-600">Build experience and give back to your community.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {jobsByType.community.map(program => renderOpportunityCard(program, 'community'))}
            </div>

            <h2 className="text-xl font-bold text-gray-800 mt-8">Agricultural Opportunities</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {jobsByType.agricultural.map(job => renderOpportunityCard(job, 'agricultural'))}
            </div>
          </div>
        )}

        {/* Tips Tab */}
        {activeTab === 'tips' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Application Tips</h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-800 mb-3">General Advice</h3>
                  <ul className="space-y-2">
                    {summerJobsData.application_tips.general_advice.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-blue-600">→</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-brand-50 rounded-lg p-4">
                  <h3 className="font-semibold text-brand-800 mb-3">Resume Essentials</h3>
                  <ul className="space-y-2">
                    {summerJobsData.application_tips.resume_essentials.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-brand-600">✓</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-6 bg-purple-50 rounded-lg p-4">
                <h3 className="font-semibold text-purple-800 mb-3">Interview Preparation</h3>
                <div className="grid sm:grid-cols-2 gap-2">
                  {summerJobsData.application_tips.interview_preparation.map((tip, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <span className="text-purple-600">★</span>
                      {tip}
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 bg-yellow-50 rounded-lg p-4">
                <h3 className="font-semibold text-yellow-800 mb-3">Required Documents</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-sm mb-2">Basic Requirements:</h4>
                    <ul className="space-y-1">
                      {summerJobsData.application_tips.required_documents.basic.map((doc, idx) => (
                        <li key={idx} className="text-sm">{doc}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-sm mb-2">For Food Service:</h4>
                    <ul className="space-y-1">
                      {summerJobsData.application_tips.required_documents.for_food_service.map((doc, idx) => (
                        <li key={idx} className="text-sm">{doc}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Parent Section */}
            {!isStudent && (
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Guide for Parents</h2>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-brand-50 rounded-lg p-4">
                    <h3 className="font-semibold text-brand-800 mb-3">What to Look For</h3>
                    <ul className="space-y-2">
                      {summerJobsData.parent_section.what_to_look_for.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <span className="text-brand-600">✓</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-red-50 rounded-lg p-4">
                    <h3 className="font-semibold text-red-800 mb-3">Red Flags to Watch</h3>
                    <ul className="space-y-2">
                      {summerJobsData.parent_section.red_flags.map((flag, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <span className="text-red-600">⚠</span>
                          {flag}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-6 bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-800 mb-3">Supporting Your Child</h3>
                  <ul className="space-y-2">
                    {summerJobsData.parent_section.supporting_your_child.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-blue-600">→</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Legal Info */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Know Your Rights</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-3">Legal Information</h3>
                  <div className="space-y-2 text-sm">
                    <div><strong>Minimum Working Age:</strong> {summerJobsData.legal_information.minimum_working_age}</div>
                    <div><strong>Full Working Age:</strong> {summerJobsData.legal_information.full_working_age}</div>
                  </div>
                  <div className="mt-3">
                    <strong className="text-sm">Restrictions Under 18:</strong>
                    <ul className="mt-2 space-y-1">
                      {summerJobsData.legal_information.restrictions_under_18.map((rule, idx) => (
                        <li key={idx} className="text-sm text-gray-600">{rule}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="bg-brand-50 rounded-lg p-4">
                  <h3 className="font-semibold text-brand-800 mb-3">Your Rights</h3>
                  <ul className="space-y-2">
                    {summerJobsData.legal_information.your_rights.map((right, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-brand-600">✓</span>
                        {right}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
